/**
 * Helpers shared by the Bookings tab, its sort controls and its PDF builders.
 *
 * One owner each: the on-screen grid, the Excel export and every PDF read the
 * same date / time / note formatting and the same comparator from here, so a
 * change to how a date prints or how blanks sort lands everywhere at once.
 */

export type Row = Record<string, unknown>;

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "16 Sep 2026" for a yyyy-mm-dd (or yyyy-mm-ddT…); anything else echoed back. */
export function prettyDate(v: unknown): string {
  if (!v || typeof v !== "string") return v == null ? "" : String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return v;
  return `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** "Wednesday 16 September 2026" for a yyyy-mm-dd; anything else echoed back. */
export function prettyDateLong(v: unknown): string {
  if (!v || typeof v !== "string") return v == null ? "" : String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return v;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  });
}

/** The HH:MM inside a time value ("12:35:00" → "12:35"); anything else echoed back. */
export function timeLabel(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  const m = /(\d{2}:\d{2})/.exec(s);
  return m ? m[1] : s;
}

/** £0.00 formatting; blank for a blank. */
export function money(v: unknown): string {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? `£${n.toFixed(2)}` : "";
}

/** Strip Stripe payment-intent ids and card noise so only human notes print. */
export function cleanNote(v: unknown): string {
  if (v == null) return "";
  let t = String(v);
  t = t.replace(/\bpi_[A-Za-z0-9]+\b(?:\s*-\s*STRIPE-[A-Z]+)?/gi, " ");
  t = t.replace(/\bSTRIPE-[A-Z]+\b/gi, " ");
  t = t.replace(/\bCard No:\s*\*+/gi, " ").replace(/\bExp:\s*[-\w/]+/gi, " ").replace(/\bIss:\s*\d+/gi, " ");
  t = t.replace(/\s+/g, " ").trim().replace(/^[\s,.-]+|[\s,-]+$/g, "").trim();
  return t;
}

/** Sort comparator: nulls last, numeric when both are numbers, otherwise
 *  case-insensitive, digit-aware string compare. (asc order; caller flips for desc.) */
export function cmpValues(a: unknown, b: unknown): number {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // nulls/blanks always sort to the end
  if (bEmpty) return -1;
  const na = typeof a === "number" ? a : Number(a);
  const nb = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(a).trim() !== "" && String(b).trim() !== "") {
    return na - nb;
  }
  return String(a).localeCompare(String(b), "en-GB", { numeric: true, sensitivity: "base" });
}
